import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { canSendInquiry } from '../../utils/membershipUtils';
import * as messageService from '../../services/messageService';
import './ChatModal.css';

const ChatModal = ({ isOpen, onClose, seller, contactPerson }) => {
  const { user } = useAuth();
  const { on, off, joinConversation, leaveConversation, isConnected } = useSocket();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const messagesEndRef = useRef(null);
  const previousConversationRef = useRef(null);

  const currentUserId = user?.id;
  const sellerId = seller?.id;

  // Get contact info - prioritize seller prop, then contactPerson
  const contact = seller ? {
    name: seller.name || seller.companyName || 'Supplier',
    role: 'Sales Manager',
    avatar: seller.logo || 'https://api.builder.io/api/v1/image/assets/TEMP/561da124ca9a57c1461a9da91c830f61842922c8?width=104',
    isVerified: seller.isVerified
  } : contactPerson || {
    name: 'Company Representative',
    role: 'Sales Manager',
    avatar: 'https://api.builder.io/api/v1/image/assets/TEMP/561da124ca9a57c1461a9da91c830f61842922c8?width=104'
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch or create conversation when modal opens
  const initializeConversation = useCallback(async () => {
    if (!isOpen || !currentUserId || !sellerId) return;

    setLoading(true);
    setError(null);
    setMessages([]);

    try {
      // First try to find existing conversation
      const conversations = await messageService.getConversations(currentUserId);
      console.log('Looking for conversation with seller:', sellerId, 'in', conversations.length, 'conversations');
      
      const existingConversation = conversations.find(conv => 
        conv.sellerId === sellerId || 
        conv.buyerId === sellerId ||
        conv.otherUserId === sellerId ||
        conv.sender?.id === sellerId
      );

      if (existingConversation) {
        console.log('Found existing conversation:', existingConversation.id);
        setConversationId(existingConversation.id);
        // Fetch messages for this conversation
        const conversationData = await messageService.getMessages(existingConversation.id, currentUserId);
        setMessages(conversationData.messages || []);
      } else {
        console.log('Creating new conversation with seller:', sellerId);
        // Create a new conversation
        const newConversation = await messageService.createConversation(
          currentUserId,
          sellerId,
          'chat',
          null,
          null
        );
        setConversationId(newConversation.id || newConversation.conversationId);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error initializing conversation:', err);
      setError('Unable to load chat. Please try again.');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [isOpen, currentUserId, sellerId]);

  useEffect(() => {
    initializeConversation();
  }, [initializeConversation]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setConversationId(null);
      setError(null);
      setMessage('');
    }
  }, [isOpen]);

  // WebSocket: Handle real-time message updates
  useEffect(() => {
    if (!currentUserId || !isConnected || !conversationId) return;

    const handleNewMessage = ({ conversationId: msgConvId, message: newMsg }) => {
      if (msgConvId === conversationId) {
        setMessages(prev => {
          // Check if message already exists (by real ID or if it's our optimistic message)
          const messageExists = prev.some(m => 
            m.id === newMsg.id || 
            (m.isOptimistic && m.content === newMsg.content && m.senderId === newMsg.senderId)
          );
          if (messageExists) {
            // If it's an optimistic message being replaced, update it
            return prev.map(m => 
              (m.isOptimistic && m.content === newMsg.content && m.senderId === newMsg.senderId) 
                ? { ...newMsg, isOptimistic: false } 
                : m
            );
          }
          // Only add if sender is not the current user (to avoid duplicates from own sends)
          if (newMsg.senderId === currentUserId) return prev;
          return [...prev, newMsg];
        });
      }
    };

    on('new_message', handleNewMessage);

    return () => {
      off('new_message', handleNewMessage);
    };
  }, [currentUserId, isConnected, conversationId, on, off]);

  // WebSocket: Join/leave conversation rooms
  useEffect(() => {
    if (!isConnected || !conversationId) return;

    if (previousConversationRef.current && previousConversationRef.current !== conversationId) {
      leaveConversation(previousConversationRef.current);
    }

    joinConversation(conversationId);
    previousConversationRef.current = conversationId;

    return () => {
      if (conversationId) {
        leaveConversation(conversationId);
      }
    };
  }, [conversationId, isConnected, joinConversation, leaveConversation]);

  const handleSendMessage = async () => {
    if (!message.trim() || sending || !conversationId || !currentUserId) return;

    // Check if Basic plan user - restrict message sending
    // Admin bypass - admin can do everything
    const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
    const planName = user?.seller?.planName || 'basic';
    if (!isAdminUser && !canSendInquiry(planName)) {
      setShowUpgradePrompt(true);
      return;
    }

    const messageText = message.trim();
    const tempId = `temp-${Date.now()}`;
    setMessage('');
    setSending(true);

    // Optimistically add message to UI
    const optimisticMessage = {
      id: tempId,
      content: messageText,
      senderId: currentUserId,
      createdAt: new Date().toISOString(),
      isOptimistic: true
    };
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const sentMessage = await messageService.sendMessage(
        conversationId,
        currentUserId,
        messageText
      );
      
      // Replace optimistic message with real one (ensure proper structure)
      const realMessage = {
        id: sentMessage.id,
        content: sentMessage.content || messageText,
        senderId: sentMessage.senderId || currentUserId,
        createdAt: sentMessage.createdAt || new Date().toISOString(),
        isOptimistic: false
      };
      
      setMessages(prev => prev.map(msg => 
        msg.id === tempId ? realMessage : msg
      ));
    } catch (err) {
      console.error('Error sending message:', err);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    // If it's already a formatted time string (like "8:00 PM" or "Just now"), return as-is
    if (typeof dateString === 'string' && 
        (dateString.includes('AM') || dateString.includes('PM') || 
         dateString.includes('ago') || dateString === 'Just now')) {
      return dateString;
    }
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
      return '';
    }
  };

  const isOwnMessage = (msg) => {
    return msg.senderId === currentUserId || msg.isSent;
  };

  if (!isOpen) return null;

  return (
    <div className="chat-modal-overlay" onClick={onClose}>
      <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="chat-modal-header">
          <div className="chat-header-content">
            <div className="chat-contact-info">
              <div className="chat-contact-avatar-container">
                <img 
                  src={contact.avatar} 
                  alt={contact.name} 
                  className="chat-contact-avatar"
                  onError={(e) => {
                    e.target.src = 'https://api.builder.io/api/v1/image/assets/TEMP/561da124ca9a57c1461a9da91c830f61842922c8?width=104';
                  }}
                />
                {contact.isVerified && (
                  <span className="chat-verified-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#1F396C">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                  </span>
                )}
              </div>
              <div className="chat-contact-details">
                <span className="chat-contact-name">{contact.name}</span>
                <span className="chat-contact-role">{contact.role}</span>
              </div>
            </div>
            <button className="chat-close-btn" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Chat Body */}
        <div className="chat-modal-body">
          {loading ? (
            <div className="chat-loading">
              <div className="chat-loading-spinner"></div>
              <span>Loading messages...</span>
            </div>
          ) : error ? (
            <div className="chat-error">
              <span>{error}</span>
              <button onClick={initializeConversation}>Retry</button>
            </div>
          ) : messages.length === 0 ? (
            <div className="chat-empty">
              <div className="chat-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="#DADADA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p>Start a conversation</p>
              <span>Send a message to {contact.name}</span>
            </div>
          ) : (
            <div className="chat-messages">
              {messages.map((msg, index) => (
                <div 
                  key={msg.id || index} 
                  className={`chat-message-row ${isOwnMessage(msg) ? 'sent' : 'received'}`}
                >
                  <div className="chat-message-content">
                    {!isOwnMessage(msg) && (
                      <div className="chat-message-avatar-wrapper">
                        <img 
                          src={contact.avatar}
                          alt={contact.name}
                          className="chat-message-avatar"
                          onError={(e) => {
                            e.target.src = 'https://api.builder.io/api/v1/image/assets/TEMP/561da124ca9a57c1461a9da91c830f61842922c8?width=104';
                          }}
                        />
                      </div>
                    )}
                    <div className="chat-message-bubble-wrapper">
                      <div className={`chat-message-bubble ${isOwnMessage(msg) ? 'sent' : 'received'} ${msg.isOptimistic ? 'sending' : ''}`}>
                        <p className="chat-message-text">{msg.content || msg.text}</p>
                      </div>
                      <span className="chat-message-time">
                        {formatTime(msg.createdAt || msg.time)}
                        {msg.isOptimistic && <span className="sending-indicator"> • Sending...</span>}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Footer - Input Area */}
        <div className="chat-modal-footer">
          {showUpgradePrompt ? (
            <div className="chat-upgrade-prompt">
              <p>⚠️ Basic plan users cannot send messages. <a href="/membership-packages">Upgrade to GCC Business or Global Enterprise</a> to access messaging.</p>
            </div>
          ) : (
          <div className="chat-input-container">
            <input
              type="text"
              placeholder="Type message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              className="chat-message-input"
              disabled={loading || !conversationId}
            />
            <div className="chat-footer-actions">
              <button className="chat-attach-btn" title="Attach file">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.5 6V17.5C16.5 19.71 14.71 21.5 12.5 21.5C10.29 21.5 8.5 19.71 8.5 17.5V5C8.5 3.62 9.62 2.5 11 2.5C12.38 2.5 13.5 3.62 13.5 5V15.5C13.5 16.05 13.05 16.5 12.5 16.5C11.95 16.5 11.5 16.05 11.5 15.5V6H10V15.5C10 16.88 11.12 18 12.5 18C13.88 18 15 16.88 15 15.5V5C15 2.79 13.21 1 11 1C8.79 1 7 2.79 7 5V17.5C7 20.54 9.46 23 12.5 23C15.54 23 18 20.54 18 17.5V6H16.5Z" fill="#1F396C"/>
                </svg>
              </button>
              <button 
                className="chat-send-btn" 
                onClick={handleSendMessage}
                disabled={!message.trim() || sending || !conversationId}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white"/>
                </svg>
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
